import React, { useState, useEffect } from "react";
import EditBookingModal from './components/EditBookingModal';
import MessageModal from './components/MessageModal';
import BulkPreviewModal from './components/BulkPreviewModal';
import Scheduling from './components/Scheduling';
import { API_BASE_URL } from './config';
import { apiGet, apiPost, apiPut, apiDelete } from './utils/api';

function App() {
  const [config, setConfig] = useState({
    rooms: {},
    treatments: [],
    therapists: {},
    equipment: {}
  });
  const [bookings, setBookings] = useState({});
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [schedulingResult, setSchedulingResult] = useState(null);
  const [formData, setFormData] = useState({
    guestName: '',
    guestId: '',  
    treatment: '',
    preferredTime: '',
    preferredTimeOfDay: 'morning',
    therapistPreference: '',
    roomPreference: '',
    date: new Date().toISOString().split('T')[0],
    genderPreference: '',
    avoidTimes: [],
    generalInstruction: '',
    treatmentInstruction: ''
  });
  const [bulkFormData, setBulkFormData] = useState({
    guestName: '',
    guestId: '',
    startDate: '',
    endDate: '',
    treatments: [],
    isPremium: false,
    rank1Therapist: false,
    generalInstruction: ''
  });
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [dateRange, setDateRange] = useState([]);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageType, setMessageType] = useState(null); // 'error' or 'schedulingResult'
  const [modalMessage, setModalMessage] = useState('');
  
  // Swap Optimization state
  const [showSwapButton, setShowSwapButton] = useState(false);
  const [swapType, setSwapType] = useState(null); // 'staff' or 'cabin'
  const [failedBookingData, setFailedBookingData] = useState(null);
  const [swapLoading, setSwapLoading] = useState(false);

  const fetchConfig = async () => {
    try {
      const response = await apiGet('/api/config');
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      setConfig(data);
    } catch (error) {
      console.error('Error fetching config:', error);
      setError('Failed to fetch configuration');
    }
  };

  const fetchBookings = async (date) => {
    try {
      setLoading(true);
      const response = await apiGet(`/api/bookings/${date}`);
      if (!response.ok) {
        throw new Error('Failed to fetch bookings');
      }
      const data = await response.json();
      setBookings(data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setError('Failed to fetch bookings');
    } finally {
      setLoading(false);
    }
  };

  // Initial setup effect - runs once
  useEffect(() => {
    const today = new Date();
    const range = [];
    for (let i = -14; i <= 14; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() + i);
      range.push(date.toISOString().split('T')[0]);
    }
    setDateRange(range);
    
    // Fetch config once at startup
    fetchConfig();
  }, []);

  // Fetch bookings when date changes
  useEffect(() => {
    if (selectedDate) {
      fetchBookings(selectedDate);
    }
  }, [selectedDate]);

  const triggerRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleSubmit = async (formData) => {
    setIsLoading(true);
    setError(null);
    setSchedulingResult(null);

    try {
      // Basic validation
      if (!formData.guestId || !formData.treatment || !formData.date) {
        setError("Please fill in all required fields (guest ID, treatment, and date)");
        return;
      }

      // Convert time strings to integers (e.g., "09:00" -> 900, "13:30" -> 1330)
      const avoidTimes = formData.avoidTimes.map(time => {
        const [hours, minutes] = time.split(':');
        return parseInt(hours) * 100 + parseInt(minutes);
      });

      const requestData = {
        guestName: formData.guestName || null,
        guestId: formData.guestId || null,
        guestRoom: formData.guestRoom ?? null,
        treatment: formData.treatment,
        date: formData.date,
        avoidTimes: avoidTimes,
        preferredTime: formData.preferredTime || null,
        preferredTimeOfDay: formData.preferredTimeOfDay === '' ? null : formData.preferredTimeOfDay,
        therapistPreference: formData.therapistPreference || null,
        roomPreference: formData.roomPreference || null,
        genderPreference: formData.genderPreference || null,
        guestGender: formData.guestGender || null,
        allowAfterOperationalEnd: !!formData.allowAfterOperationalEnd,
        generalInstruction: formData.generalInstruction || '',
        treatmentInstruction: formData.treatmentInstruction || ''
      };

      // Convert empty strings to null
      Object.keys(requestData).forEach(key => {
        if (requestData[key] === '') {
          requestData[key] = null;
        }
      });

      console.log("Submitting treatment request:", requestData);

      // First, get preview of available slots
      const previewResponse = await apiPost('/api/schedule/preview', requestData);

      const previewData = await previewResponse.json();
      console.log('Schedule Preview Response:', previewData);

      if (!previewResponse.ok || previewData.error) {
        if (
          previewData.error_type === 'AFTER_HOURS_CONFIRMATION_REQUIRED'
          && !requestData.allowAfterOperationalEnd
        ) {
          const confirmed = window.confirm(
            `${previewData.error}\n\nBook this after-hours slot anyway?`,
          );
          if (confirmed) {
            await handleSubmit({
              ...formData,
              allowAfterOperationalEnd: true,
            });
            return;
          }
        }

        setMessageType('error');
        const errorMessage = previewData.error || previewData.detail || 'Failed to get scheduling preview';
        setModalMessage(errorMessage);
        setShowMessageModal(true);
        
        // Detect if failure is due to staff/room unavailability
        // Enable swap optimization
        const errorLower = errorMessage.toLowerCase();
        if (errorLower.includes('therapist') || errorLower.includes('staff') || errorLower.includes('no slots')) {
          setSwapType('staff');
          setShowSwapButton(true);
          setFailedBookingData(requestData);
        } else if (errorLower.includes('room') || errorLower.includes('cabin')) {
          setSwapType('cabin');
          setShowSwapButton(true);
          setFailedBookingData(requestData);
        }
        return;
      }

      // Clear any previous errors and set the scheduling result
      setError(null);
      setSchedulingResult(previewData);
      setMessageType('schedulingResult');
      setShowMessageModal(true);

    } catch (error) {
      console.error('Error getting schedule preview:', error);
      setMessageType('error');
      setModalMessage(error.message || 'Failed to get scheduling preview');
      setShowMessageModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmBooking = async () => {
    try {
      if (!schedulingResult) {
        return;
      }

      setIsLoading(true);
      const bookingData = {
        treatment: schedulingResult.treatment,
        date: schedulingResult.date,
        startTime: schedulingResult.startTime,
        endTime: schedulingResult.endTime,
        guestName: schedulingResult.guestName,
        guestId: schedulingResult.guestId,
        therapist: schedulingResult.therapist,
        therapists: schedulingResult.therapists || [], // Multi-therapist support
        room: schedulingResult.room,
        generalInstruction: schedulingResult.generalInstruction || '',
        treatmentInstruction: schedulingResult.treatmentInstruction || ''
      };

      // Confirm the booking
      const confirmResponse = await apiPost('/api/schedule/confirm', bookingData);

      const confirmData = await confirmResponse.json();
      console.log('Confirm Booking Response:', confirmData);

      if (!confirmResponse.ok || confirmData.error) {
        setMessageType('error');
        setModalMessage(confirmData.detail || 'Failed to confirm booking');
        setShowMessageModal(true);
        return;
      }

      // Show success message with booking details
      const therapistDisplay = (() => {
        const therapists = bookingData.therapists || [];
        const therapistsCount = bookingData.therapistsCount || (therapists.length > 0 ? therapists.length : 1);
        if (therapistsCount > 1) {
          const allNames = therapists.map(t => t.name).join(', ');
          return `Therapists: ${allNames} (${therapistsCount} therapists)`;
        }
        return `Therapist: ${bookingData.therapist}`;
      })();
      
      const successMessage = `
        ✅ Booking Confirmed Successfully!
        
        Guest: ${bookingData.guestName} (${bookingData.guestId})
        Treatment: ${bookingData.treatment}
        Date: ${bookingData.date}
        Time: ${bookingData.startTime} - ${bookingData.endTime}
        ${therapistDisplay}
        Room: ${bookingData.room}
        ${bookingData.generalInstruction ? `\nGeneral Instructions: ${bookingData.generalInstruction}` : ''}
        ${bookingData.treatmentInstruction ? `Treatment Instructions: ${bookingData.treatmentInstruction}` : ''}
      `.trim();

      // Close the preview modal first
      setShowMessageModal(false);
      setSchedulingResult(null);
      
      // Wait a brief moment then show success modal
      setTimeout(() => {
        setMessageType('success');
        setModalMessage(successMessage);
        setShowMessageModal(true);
      }, 100);

      // Clear form and trigger refresh
      setFormData({
        guestName: '',
        guestId: '',
        treatment: '',
        preferredTime: '',
        preferredTimeOfDay: 'morning',
        therapistPreference: '',
        roomPreference: '',
        date: new Date().toISOString().split('T')[0],
        genderPreference: '',
        avoidTimes: [],
        generalInstruction: '',
        treatmentInstruction: ''
      });
      
      triggerRefresh(); // Trigger refresh in CalendarPanel
      setIsLoading(false);
    } catch (error) {
      console.error('Error confirming booking:', error);
      setMessageType('error');
      setModalMessage(error.message || 'Failed to confirm booking');
      setShowMessageModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Swap Optimization Handler
  const handleSwapOptimize = async () => {
    try {
      if (!failedBookingData) {
        return;
      }

      setSwapLoading(true);
      console.log('🔄 Attempting swap optimization:', swapType);

      // Extract data for swap request
      const swapRequest = {
        type: swapType, // 'staff' or 'cabin'
        service: failedBookingData.treatment,
        date: failedBookingData.date,
        time: '14:00', // Default time - should be extracted from preferences if available
        guest_name: failedBookingData.guestName,
        guest_id: failedBookingData.guestId,
        gender_requirement: failedBookingData.guestGender || failedBookingData.genderPreference
      };

      const response = await apiPost('/api/schedule/optimize-swap', swapRequest);

      const result = await response.json();
      console.log('Swap Optimization Response:', result);

      if (!response.ok || !result.success) {
        setMessageType('error');
        setModalMessage(result.message || 'Swap optimization failed');
        setShowMessageModal(true);
        setShowSwapButton(false); // Hide button after failed attempt
        return;
      }

      // Success! Show the result
      const swapDetails = result.swaps && result.swaps.length > 0 ? result.swaps[0] : null;
      const swapMessage = swapDetails 
        ? `✅ Booking created successfully!\n\n📋 Swapped Details:\n• Moved ${swapDetails.guest_name}'s ${swapDetails.service}\n• From: ${swapDetails.from_therapist}\n• To: ${swapDetails.to_therapist}\n• Time: ${swapDetails.time} (unchanged)\n\n🎉 Your booking is now confirmed!`
        : '✅ Booking created successfully through swap optimization!';

      setMessageType('success');
      setModalMessage(swapMessage);
      setShowMessageModal(true);
      setShowSwapButton(false);
      setFailedBookingData(null);

      // Refresh bookings to show the new booking
      fetchBookings(selectedDate);
      setRefreshTrigger(prev => prev + 1);

    } catch (error) {
      console.error('Error during swap optimization:', error);
      setMessageType('error');
      setModalMessage(error.message || 'Failed to execute swap optimization');
      setShowMessageModal(true);
    } finally {
      setSwapLoading(false);
    }
  };

  // removed unused handleCancelBooking

  const handleBulkSubmit = async (formData) => {
    setIsLoading(true);
    setError(null);
    setSchedulingResult(null);

    try {
      // Basic validation
      if (!formData.guestName || !formData.guestId || !formData.startDate || !formData.endDate || !formData.treatments.length) {
        setError("Please fill in all required fields (guest name, guest ID, start date, end date, and select at least one treatment)");
        return;
      }

      // First, get preview of available slots
      const previewResponse = await apiPost('/api/schedule/bulk/preview', {
        guestName: formData.guestName,
        guestId: formData.guestId,
        startDate: formData.startDate,
        endDate: formData.endDate,
        treatments: formData.treatments,
        avoidTimes: [],
        generalInstruction: formData.generalInstruction || ''
      });

      const previewData = await previewResponse.json();

      if (!previewResponse.ok) {
        // Handle validation errors
        if (previewResponse.status === 422 && previewData.detail) {
          if (Array.isArray(previewData.detail)) {
            // Format validation errors
            const errors = previewData.detail.map(err => {
              const field = err.loc[err.loc.length - 1];
              return `${field}: ${err.msg}`;
            });
            setError(`Validation errors: ${errors.join(', ')}`);
          } else {
            setError(previewData.detail);
          }
        } else {
          setError(previewData.detail || 'Failed to get scheduling preview');
        }
        return;
      }

      if (!previewData.success || (previewData.failedSlots && previewData.failedSlots.length > 0)) {
        const failedMessage = previewData.failedSlots.length > 0 
          ? `Some slots could not be scheduled: ${previewData.failedSlots.map(slot => 
              `${slot.treatment} on ${slot.date} (${slot.reason})`
            ).join(', ')}`
          : 'Failed to schedule treatments';
        setError(failedMessage);
        return;
      }

      // Set the scheduling result to show preview
      setSchedulingResult(previewData);
      setMessageType('schedulingResult');
      setShowMessageModal(true);

    } catch (error) {
      console.error('Error getting schedule preview:', error);
      setError(error.message || 'Failed to get scheduling preview');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkConfirm = async (previewData) => {
    if (!schedulingResult || !schedulingResult.proposedSchedule) {
      setError('No preview data available');
      return;
    }

    try {
      setIsLoading(true);
      // Get guest info from the first slot since all slots have same guest
      const firstSlot = schedulingResult.proposedSchedule[0];
      
      // Confirm the booking
      const confirmResponse = await apiPost('/api/schedule/bulk/confirm', {
        guestName: firstSlot.guestName,
        guestId: firstSlot.guestId,
        slots: schedulingResult.proposedSchedule,
        requireAll: true,
        generalInstruction: previewData.generalInstruction || '',
        treatmentInstructions: previewData.treatmentInstructions || {}
      });

      const confirmData = await confirmResponse.json();

      if (!confirmResponse.ok || confirmData.error) {
        setError(confirmData.detail || 'Failed to confirm bulk booking');
        return;
      }

      // Clear form and show success
      setBulkFormData({
        guestName: '',
        guestId: '',
        startDate: '',
        endDate: '',
        treatments: [],
        isPremium: false,
        rank1Therapist: false,
        generalInstruction: ''
      });
      
      setSchedulingResult(null);
      setShowMessageModal(false);
      setSuccess('Bulk booking confirmed successfully!');
      triggerRefresh();

    } catch (error) {
      console.error('Error confirming bulk booking:', error);
      setError(error.message || 'Failed to confirm bulk booking');
    } finally {
      setIsLoading(false);
    }
  };

  // removed unused parseTimeToMinutes

  const handleBookingSelect = (booking) => {
    setSelectedBooking(booking);
    setShowEditModal(true);
  };

  const handleSchedulingComplete = ({ success, error }) => {
    if (success) {
      setSuccess('Booking updated successfully!');
      setShowEditModal(false);
      setSelectedBooking(null);
      triggerRefresh();
    } else if (error) {
      setError(error);
    }
  };

  const handleDeleteBooking = async (booking) => {
    try {
      const response = await apiDelete(`/api/bookings/${booking.id}`);

      if (!response.ok) {
        throw new Error('Failed to delete booking');
      }

      setSuccess('Booking deleted successfully!');
      setShowEditModal(false);
      setSelectedBooking(null);
      triggerRefresh();
    } catch (error) {
      setError(error.message);
    }
  };

  const handleModifyBooking = async (editedBooking) => {
    try {
      if (!selectedBooking) return;

      const response = await apiPut(`/api/bookings/${selectedBooking.id}`, editedBooking);

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to modify booking');
      }

      setSuccess('Booking modified successfully');
      setShowEditModal(false);
      setSelectedBooking(null);
      triggerRefresh();
      if (editedBooking.date !== selectedDate) {
        triggerRefresh();
      }
    } catch (error) {
      setError(error.message);
    }
  };

  // removed unused formatTime

  const LoadingSpinner = () => (
    <div className="flex h-screen items-center justify-center">
      <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
    </div>
  );

  // Removed inline MessageModal - using imported component from components/MessageModal.js instead

  if (loading) return <LoadingSpinner />;
  // Remove the global error display
  // if (error) return <ErrorDisplay message={error} />;

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">
      {/* Message Modal for Individual Booking */}
      <MessageModal 
        isOpen={showMessageModal && messageType !== null}
        onClose={() => {
          setShowMessageModal(false);
          setMessageType(null);
          setModalMessage(null);
        }}
        type={messageType}
        message={modalMessage}
        schedulingResult={schedulingResult}
        onConfirm={handleConfirmBooking}
        // Swap Optimization props
        showSwapButton={showSwapButton}
        swapType={swapType}
        onSwapOptimize={handleSwapOptimize}
        swapLoading={swapLoading}
      />

      {/* Bulk Preview Modal */}
      <BulkPreviewModal 
        isOpen={showMessageModal && schedulingResult && Array.isArray(schedulingResult)}
        onClose={() => {
          setShowMessageModal(false);
          setMessageType(null);
          setModalMessage(null);
        }}
        schedulingResult={schedulingResult}
        onConfirm={handleBulkConfirm}
      />
      
      {/* Scheduling Container */}
      <Scheduling
        config={config}
        formData={formData}
        bulkFormData={bulkFormData}
        onSubmit={handleSubmit}
        onBulkSubmit={handleBulkSubmit}
        isLoading={isLoading}
        error={error}
        success={success}
        schedulingResult={schedulingResult}
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
        dateRange={dateRange}
        onEditBooking={handleBookingSelect}
        loading={loading}
        refreshTrigger={refreshTrigger}
      />

      {/* Edit Modal */}
      {showEditModal && selectedBooking && (
        <EditBookingModal
          show={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedBooking(null);
          }}
          booking={selectedBooking}
          config={config}
          onSchedulingComplete={handleSchedulingComplete}
          onDelete={handleDeleteBooking}
        />
      )}
    </div>
  );
}

export default App;
